// frontend/src/App.js - Updated with separate admin route
import React, { useState, useEffect, useRef } from 'react';
import LandingPage from './pages/LandingPage';
import LoginFlow from './pages/LoginPage';
import SignUpFlow from './pages/SignUpPage';
import Dashboard from './pages/Dashboard';
import UserBetsPage from './pages/UserBetsPage';
import WalletPage from './pages/WalletPage';
import ProfilePage from './pages/ProfilePage';
import AdminDashboard from './pages/AdminDashboard';
import AdminLogin from './pages/AdminLogin';
import BetCopilotPage from './pages/BetCopilotPage';
import KycVerificationPage from './pages/KycVerificationPage';
import LoadingSplash from './components/LoadingSplash';
import { setActiveCurrency } from './utils/money';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
console.log('API_BASE_URL:', API_BASE_URL);

// On-screen debug overlay: opt-in only (set REACT_APP_DEBUG_OVERLAY=1).
// Logs still go to the browser console; they just don't paint over the UI.
const DEBUG_OVERLAY = process.env.REACT_APP_DEBUG_OVERLAY === '1';

function App() {
  const [currentPage, setCurrentPage] = useState('landing');
  const [user, setUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [debugLog, setDebugLog] = useState([]);
  // Post-login/signup/cold-start branded loading splash, gated on the board.
  const [boardLoading, setBoardLoading] = useState(false);
  const [boardReady, setBoardReady] = useState(false);
  const boardLoadingRef = useRef(false);

  // Show the loading splash while the dashboard loads its board. Idempotent so
  // login + session-restore can't double-trigger it. The max timer is a
  // fail-safe so a slow/failed fetch can never trap the user.
  const beginBoardLoad = () => {
    if (boardLoadingRef.current) return;
    boardLoadingRef.current = true;
    setBoardReady(false);
    setBoardLoading(true);
    // Generous fail-safe: a cold-cache board fetch can take >10s, and the splash
    // is meant to cover exactly that. onBoardReady dismisses it the instant the
    // board is actually ready (usually ~2s warm).
    setTimeout(() => setBoardReady(true), 20000);
  };

  // Abort the splash immediately (no fade) - used when an auth attempt the
  // splash was covering fails, so the form + error can show right away.
  const cancelBoardLoad = () => {
    boardLoadingRef.current = false;
    setBoardLoading(false);
    setBoardReady(false);
  };

  // Debug function
  const addDebugLog = (message) => {
    console.log('🔍 DEBUG:', message);
    setDebugLog(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  // Route detection and initialization
  useEffect(() => {
    const currentPath = window.location.pathname;
    addDebugLog(`Initial route detected: ${currentPath}`);
    
    // Check for admin routes first
    if (currentPath === '/admin' || currentPath === '/admin/') {
      addDebugLog('Admin route detected - setting admin-login page');
      setCurrentPage('admin-login');
      setLoading(false);
      return;
    }
    
    if (currentPath === '/admin/dashboard') {
      addDebugLog('Admin dashboard route detected');
      // Check if admin is logged in
      const savedAdminUser = localStorage.getItem('playerbet_admin');
      const savedAdminToken = localStorage.getItem('playerbet_admin_token');
      if (savedAdminUser && savedAdminToken) {
        try {
          const adminData = JSON.parse(savedAdminUser);
          if (adminData && adminData.username) {
            setAdminUser(adminData);
            setCurrentPage('admin-dashboard');
            setLoading(false);
            return;
          }
        } catch (error) {
          addDebugLog(`Error parsing admin data: ${error.message}`);
          localStorage.removeItem('playerbet_admin');
        }
      }
      // Redirect to admin login if not authenticated
      setCurrentPage('admin-login');
      window.history.replaceState({}, '', '/admin');
      setLoading(false);
      return;
    }
    
    // Handle other routes
    if (currentPath === '/login') {
      setCurrentPage('login');
      setLoading(false);
      return;
    }
    
    if (currentPath === '/signup') {
      setCurrentPage('signup');
      setLoading(false);
      return;
    }
    
    if (currentPath === '/dashboard') {
      // Check if user is logged in
      const savedUser = localStorage.getItem('playerbet_user');
      const savedToken = localStorage.getItem('playerbet_token');
      
      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser);
          if (userData && userData.id) {
            setUser(userData);
            // Already logged in and landing on the dashboard: cover the board
            // load with the branded splash too, so the loading state is never seen.
            beginBoardLoad();
            setCurrentPage('dashboard');
            setLoading(false);
            return;
          }
        } catch (error) {
          addDebugLog(`Error parsing user data: ${error.message}`);
        }
      }
      // Redirect to landing if not authenticated
      setCurrentPage('landing');
      window.history.replaceState({}, '', '/');
      setLoading(false);
      return;
    }
    
    if (currentPath === '/profile') {
      // Check if user is logged in
      const savedUser = localStorage.getItem('playerbet_user');
      const savedToken = localStorage.getItem('playerbet_token');

      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser);
          if (userData && userData.id) {
            setUser(userData);
            setCurrentPage('profile');
            setLoading(false);
            return;
          }
        } catch (error) {
          addDebugLog(`Error parsing user data: ${error.message}`);
        }
      }
      // Redirect to landing if not authenticated
      setCurrentPage('landing');
      window.history.replaceState({}, '', '/');
      setLoading(false);
      return;
    }

    if (currentPath === '/wallet') {
      // Check if user is logged in
      const savedUser = localStorage.getItem('playerbet_user');
      const savedToken = localStorage.getItem('playerbet_token');

      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser);
          if (userData && userData.id) {
            setUser(userData);
            setCurrentPage('wallet');
            setLoading(false);
            return;
          }
        } catch (error) {
          addDebugLog(`Error parsing user data: ${error.message}`);
        }
      }
      // Redirect to landing if not authenticated
      setCurrentPage('landing');
      window.history.replaceState({}, '', '/');
      setLoading(false);
      return;
    }

    if (currentPath === '/my-bets') {
      // Check if user is logged in
      const savedUser = localStorage.getItem('playerbet_user');
      const savedToken = localStorage.getItem('playerbet_token');
      
      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser);
          if (userData && userData.id) {
            setUser(userData);
            setCurrentPage('user-bets');
            setLoading(false);
            return;
          }
        } catch (error) {
          addDebugLog(`Error parsing user data: ${error.message}`);
        }
      }
      // Redirect to landing if not authenticated
      setCurrentPage('landing');
      window.history.replaceState({}, '', '/');
      setLoading(false);
      return;
    }
    
    if (currentPath === '/bet-copilot') {
      const savedUser = localStorage.getItem('playerbet_user');
      const savedToken = localStorage.getItem('playerbet_token');
      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser);
          if (userData && userData.id) {
            setUser(userData);
            setCurrentPage('bet-copilot');
            setLoading(false);
            return;
          }
        } catch (error) {
          addDebugLog(`Error parsing user data: ${error.message}`);
        }
      }
      setCurrentPage('landing');
      window.history.replaceState({}, '', '/');
      setLoading(false);
      return;
    }

    if (currentPath === '/kyc') {
      const savedUser = localStorage.getItem('playerbet_user');
      const savedToken = localStorage.getItem('playerbet_token');
      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser);
          if (userData && userData.id) {
            setUser(userData);
            setCurrentPage('kyc');
            setLoading(false);
            return;
          }
        } catch (error) {
          addDebugLog(`Error parsing user data: ${error.message}`);
        }
      }
      setCurrentPage('landing');
      window.history.replaceState({}, '', '/');
      setLoading(false);
      return;
    }

    // Default to checking auth status for landing page
    addDebugLog('No specific route - checking auth status');
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if user is already logged in
  const checkAuthStatus = () => {
    addDebugLog('Checking auth status');
    
    const checkAuthStatus = () => {
      const savedUser = localStorage.getItem('playerbet_user');
      const savedToken = localStorage.getItem('playerbet_token');
      const savedAdminUser = localStorage.getItem('playerbet_admin');
      
      addDebugLog(`Saved user exists: ${!!savedUser}`);
      addDebugLog(`Saved token exists: ${!!savedToken}`);
      addDebugLog(`Saved admin exists: ${!!savedAdminUser}`);
      
      // NOTE: deliberately do NOT auto-redirect to the admin dashboard from the
      // public root based on a saved admin session. A leftover admin session in
      // localStorage was hijacking playerbet.app and sending every visit to the
      // admin dashboard, making the landing page unreachable. The admin panel is
      // reachable only via the explicit /admin route (handled earlier).

      // Check for regular user session
      if (savedUser && savedToken) {
        try {
          const userData = JSON.parse(savedUser);
          addDebugLog(`Parsed user data: ${JSON.stringify(userData)}`);
          
          if (userData && userData.id) {
            addDebugLog(`Setting user and navigating to dashboard`);
            setUser(userData);
            setCurrentPage('dashboard');
            window.history.replaceState({}, '', '/dashboard');
          } else {
            addDebugLog('User data missing ID - clearing storage');
            localStorage.removeItem('playerbet_user');
            localStorage.removeItem('playerbet_token');
            setCurrentPage('landing');
            window.history.replaceState({}, '', '/');
          }
        } catch (error) {
          addDebugLog(`Error parsing user data: ${error.message}`);
          localStorage.removeItem('playerbet_user');
          localStorage.removeItem('playerbet_token');
          setCurrentPage('landing');
          window.history.replaceState({}, '', '/');
        }
      } else {
        addDebugLog('No saved user/token - staying on landing');
        setCurrentPage('landing');
        window.history.replaceState({}, '', '/');
      }
      
      setLoading(false);
    };

    // Small delay to see what's happening
    setTimeout(checkAuthStatus, 100);
  };

  // Watch for state changes
  useEffect(() => {
    addDebugLog(`User state changed: ${user ? `ID ${user.id}` : 'null'}`);
    // Drive all money formatting off the logged-in account's currency.
    setActiveCurrency(user?.currency);
  }, [user]);

  useEffect(() => {
    addDebugLog(`Admin state changed: ${adminUser ? `Admin ${adminUser.username}` : 'null'}`);
  }, [adminUser]);

  useEffect(() => {
    addDebugLog(`Page changed to: ${currentPage}`);
  }, [currentPage]);

  // Navigation functions
  const navigateToLogin = () => {
    addDebugLog('Navigating to login');
    setCurrentPage('login');
    // Update URL without page reload
    window.history.pushState({}, '', '/login');
  };
  
  const navigateToSignUp = () => {
    addDebugLog('Navigating to signup');
    setCurrentPage('signup');
    window.history.pushState({}, '', '/signup');
  };
  
  const navigateToDashboard = () => {
    addDebugLog('Navigating to dashboard');
    setCurrentPage('dashboard');
    window.history.pushState({}, '', '/dashboard');
  };
  
  const navigateToLanding = () => {
    addDebugLog('Navigating to landing');
    setCurrentPage('landing');
    window.history.pushState({}, '', '/');
  };

  const navigateToUserBets = () => {
    addDebugLog('Navigating to user bets');
    setCurrentPage('user-bets');
    window.history.pushState({}, '', '/my-bets');
  };

  const navigateToWallet = () => {
    addDebugLog('Navigating to wallet');
    setCurrentPage('wallet');
    window.history.pushState({}, '', '/wallet');
  };

  const navigateToProfile = () => {
    addDebugLog('Navigating to profile');
    setCurrentPage('profile');
    window.history.pushState({}, '', '/profile');
  };

  const navigateToCopilot = () => {
    addDebugLog('Navigating to bet copilot');
    setCurrentPage('bet-copilot');
    window.history.pushState({}, '', '/bet-copilot');
  };

  const navigateToKyc = () => {
    addDebugLog('Navigating to KYC verification');
    setCurrentPage('kyc');
    window.history.pushState({}, '', '/kyc');
  };

  const navigateToAdminDashboard = () => {
    addDebugLog('Navigating to admin dashboard');
    setCurrentPage('admin-dashboard');
    window.history.pushState({}, '', '/admin/dashboard');
  };

  // Login handler
  const handleLogin = async (loginData) => {
    // Start the branded splash on tap so one animation covers the auth call AND
    // the board load. Cancelled below if login fails.
    beginBoardLoad();
    try {
      addDebugLog(`Starting login attempt for: ${loginData.email}`);

      const response = await fetch(`${API_BASE_URL}/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(loginData),
      });

      addDebugLog(`Login API response status: ${response.status}`);

      const result = await response.json();
      addDebugLog(`Login API result: ${JSON.stringify(result)}`);

      if (response.ok && result.user && result.user.id) {
        const userWithId = {
          id: result.user.id,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          email: result.user.email,
          username: result.user.username,
          balance: result.user.balance,
          currency: result.user.currency,
          name: result.user.name || `${result.user.firstName} ${result.user.lastName}`
        };
        
        addDebugLog(`Created user object with ID: ${userWithId.id}`);
        
        // Store in localStorage first
        localStorage.setItem('playerbet_user', JSON.stringify(userWithId));
        localStorage.setItem('playerbet_token', result.token);
        addDebugLog('Saved to localStorage');
        
        // Then set state
        setUser(userWithId);
        addDebugLog('Set user state');

        // Splash is already up (started on tap); it keeps going into the board load.
        // Wait a bit then navigate
        setTimeout(() => {
          addDebugLog('About to navigate to dashboard');
          navigateToDashboard();
        }, 100);

        return { success: true };
      } else {
        cancelBoardLoad();
        addDebugLog(`Login failed: ${result.message}`);
        return { success: false, message: result.message || 'Login failed' };
      }
    } catch (error) {
      cancelBoardLoad();
      addDebugLog(`Login error: ${error.message}`);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  // Admin login handler
  const handleAdminLogin = (adminData) => {
    addDebugLog(`Admin login successful: ${adminData.username}`);
    setAdminUser(adminData);
    localStorage.setItem('playerbet_admin', JSON.stringify(adminData));
    navigateToAdminDashboard();
  };

  const handleSignUp = async (signUpData) => {
    // Start the branded splash on tap so one animation covers registration AND
    // the board load. Cancelled below if signup fails.
    beginBoardLoad();
    try {
      addDebugLog(`Starting signup for: ${signUpData.email}`);

      // Format date properly
      const formattedData = {
        ...signUpData,
        dateOfBirth: new Date(signUpData.dateOfBirth).toISOString()
      };
      
      const response = await fetch(`${API_BASE_URL}/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedData)
      });
      
      const result = await response.json();
      addDebugLog(`Signup API result: ${JSON.stringify(result)}`);
      
      if (response.ok && result.user && result.user.id) {
        const userWithId = {
          id: result.user.id,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          email: result.user.email,
          username: result.user.username,
          balance: result.user.balance,
          currency: result.user.currency,
          name: result.user.name || `${result.user.firstName} ${result.user.lastName}`
        };
        
        // Store and set user (splash already up from tap).
        localStorage.setItem('playerbet_user', JSON.stringify(userWithId));
        setUser(userWithId);
        navigateToDashboard();

        return { success: true };
      } else {
        cancelBoardLoad();
        return { success: false, message: result.message || 'Signup failed' };
      }
    } catch (error) {
      cancelBoardLoad();
      addDebugLog(`Signup error: ${error.message}`);
      return { success: false, message: 'Network error. Please try again.' };
    }
  };

  const handleLogout = () => {
    addDebugLog('Logging out');
    setUser(null);
    setAdminUser(null);
    localStorage.removeItem('playerbet_user');
    localStorage.removeItem('playerbet_token');
    localStorage.removeItem('playerbet_admin');
    localStorage.removeItem('playerbet_admin_token');
    
    // Navigate based on current context
    if (currentPage.includes('admin')) {
      setCurrentPage('admin-login');
      window.history.pushState({}, '', '/admin');
    } else {
      navigateToLanding();
    }
  };

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      
      if (path === '/' || path === '') {
        setCurrentPage('landing');
      } else if (path === '/login') {
        setCurrentPage('login');
      } else if (path === '/signup') {
        setCurrentPage('signup');
      } else if (path === '/dashboard') {
        setCurrentPage('dashboard');
      } else if (path === '/my-bets') {
        setCurrentPage('user-bets');
      } else if (path === '/wallet') {
        setCurrentPage('wallet');
      } else if (path === '/profile') {
        setCurrentPage('profile');
      } else if (path === '/bet-copilot') {
        setCurrentPage('bet-copilot');
      } else if (path === '/kyc') {
        setCurrentPage('kyc');
      } else if (path === '/admin' || path === '/admin/') {
        setCurrentPage('admin-login');
      } else if (path === '/admin/dashboard') {
        setCurrentPage('admin-dashboard');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Show debug info if needed
  const showDebugInfo = debugLog.some(log => log.includes('ERROR') || log.includes('failed'));

  // Show loading screen
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#000',
        color: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #374151',
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '16px', color: '#9ca3af' }}>Loading...</p>
        
        {/* Debug log */}
        {DEBUG_OVERLAY && debugLog.length > 0 && (
          <div style={{
            marginTop: '20px',
            backgroundColor: '#1a1a1a',
            padding: '15px',
            borderRadius: '8px',
            maxWidth: '600px',
            maxHeight: '200px',
            overflow: 'auto'
          }}>
            <div style={{ fontSize: '12px', fontFamily: 'monospace' }}>
              {debugLog.map((log, index) => (
                <div key={index} style={{ marginBottom: '5px' }}>{log}</div>
              ))}
            </div>
          </div>
        )}
        
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Render current page with debug info
  const renderCurrentPage = () => {
    const pageComponent = (() => {
      switch (currentPage) {
        case 'landing':
          return (
            <LandingPage 
              onLogin={navigateToLogin}
              onSignUp={navigateToSignUp}
              // Remove the admin navigation prop - no longer needed
            />
          );
        case 'login':
          return (
            <LoginFlow 
              onLogin={handleLogin}
              onSignUp={navigateToSignUp}
              onBack={navigateToLanding}
            />
          );
        case 'signup':
          return (
            <SignUpFlow
              onSignUp={handleSignUp}
              onLogin={navigateToLogin}
              onBack={navigateToLanding}
            />
          );
        case 'dashboard':
          return (
            <Dashboard
              user={user}
              onLogout={handleLogout}
              onNavigateToUserBets={navigateToUserBets}
              onNavigateToWallet={navigateToWallet}
              onNavigateToProfile={navigateToProfile}
              onNavigateToCopilot={navigateToCopilot}
              onBoardReady={() => setBoardReady(true)}
              // Remove admin navigation from dashboard too
            />
          );
        case 'user-bets':
          return (
            <UserBetsPage
              user={user}
              onBack={navigateToDashboard}
            />
          );
        case 'wallet':
          return (
            <WalletPage
              user={user}
              onBack={navigateToDashboard}
            />
          );
        case 'bet-copilot':
          return (
            <BetCopilotPage
              user={user}
              onBack={navigateToDashboard}
            />
          );
        case 'kyc':
          return (
            <KycVerificationPage
              user={user}
              onBack={navigateToDashboard}
            />
          );
        case 'profile':
          return (
            <ProfilePage
              user={user}
              onBack={navigateToDashboard}
              onLogout={handleLogout}
              onUserUpdated={setUser}
              onNavigateToKyc={navigateToKyc}
            />
          );
        case 'admin-login':
          return (
            <AdminLogin 
              onAdminLogin={handleAdminLogin}
              onBack={() => {
                // Navigate to appropriate page based on user state
                if (user) {
                  navigateToDashboard();
                } else {
                  navigateToLanding();
                }
              }}
            />
          );
        case 'admin-dashboard':
          return adminUser ? (
            <AdminDashboard 
              onLogout={handleLogout}
              adminUser={adminUser}
            />
          ) : (
            <AdminLogin onAdminLogin={handleAdminLogin} />
          );
        default:
          return <LandingPage onLogin={navigateToLogin} onSignUp={navigateToSignUp} />;
      }
    })();

    return (
      <div>
        {pageComponent}
        
        {/* Debug Panel - opt-in only via REACT_APP_DEBUG_OVERLAY */}
        {DEBUG_OVERLAY && (showDebugInfo || debugLog.length > 10) && (
          <div style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            backgroundColor: '#1a1a1a',
            color: 'white',
            padding: '15px',
            borderRadius: '8px',
            maxWidth: '400px',
            maxHeight: '300px',
            overflow: 'auto',
            zIndex: 1000,
            fontSize: '12px',
            fontFamily: 'monospace'
          }}>
            <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
              Debug Log ({currentPage} page):
              <button 
                onClick={() => setDebugLog([])}
                style={{
                  marginLeft: '10px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  cursor: 'pointer'
                }}
              >
                Clear
              </button>
            </div>
            {debugLog.slice(-15).map((log, index) => (
              <div key={index} style={{ 
                marginBottom: '3px',
                color: log.includes('ERROR') || log.includes('failed') ? '#ff6b6b' : 
                      log.includes('SUCCESS') || log.includes('successful') ? '#51cf66' : 'white'
              }}>
                {log}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="App">
      {renderCurrentPage()}
      {boardLoading && (
        <LoadingSplash
          ready={boardReady}
          onFinish={() => {
            boardLoadingRef.current = false;
            setBoardLoading(false);
            setBoardReady(false);
          }}
        />
      )}
    </div>
  );
}

export default App;